import { useMemo, useState } from 'react';
import {
  Plus, Send, Eye, Target, Globe, Mail, Phone, PhoneCall, Ban,
  AlertTriangle, ExternalLink, Trash2, Building2, BookOpen,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { relative } from '@/lib/format';
import {
  Badge, Button, Card, Checkbox, ConfirmDialog, EmptyState, Field, Input,
  LoadingBlock, Modal, PageHeader, SearchInput, Select, Tabs, Textarea, useToast,
} from '@/components/ui';
import type {
  EmailTemplateList, Lead, LeadCompanyType, LeadDetail, LeadListResponse,
  LeadOutreachPreview, LeadSource, LeadStatus, OutreachResult,
} from '@shared/types';
import { CallPanel } from '@/components/CallPanel';
import LeadPlaybook from './LeadPlaybook';

const STATUS_TONE: Record<LeadStatus, 'neutral' | 'info' | 'ok' | 'warn' | 'danger' | 'brand'> = {
  new: 'brand',
  qualified: 'info',
  contacted: 'info',
  replied: 'ok',
  won: 'ok',
  lost: 'neutral',
  unsubscribed: 'danger',
};

const BLANK_LEAD = {
  name: '',
  contact_name: '',
  email: '',
  phone: '',
  website: '',
  city: '',
  notes: '',
  source: 'manual' as LeadSource,
  /* Where you found them. The most useful single thing to know later. */
  source_detail: '',
  /*
   * Defaults to unknown rather than to either answer. It decides whether a
   * cold email to them is lawful, so a guess in either direction is the one
   * mistake on this form worth preventing.
   */
  company_type: 'unknown' as LeadCompanyType,
  company_number: '',
};

const SOURCE_LABEL: Record<LeadSource, string> = {
  manual: 'Found it myself',
  referral: 'Referred by someone',
  event: 'Met at an event',
  import: 'Imported',
};

const COMPANY_TYPE_LABEL: Record<LeadCompanyType, string> = {
  limited_company: 'Limited company — may be emailed',
  sole_trader: 'Sole trader — needs consent',
  unknown: "Don't know yet",
};

/**
 * Finding people to sell to, and writing to them.
 *
 * ── The shape of this screen is one rule ──────────────────────────────────
 *
 * A customer asked to hear from us; a lead did not. So searching fills a list
 * and stops. A person reads the list, ticks who is worth writing to, picks a
 * template, looks at the preview — including the exact opt-out line that will
 * be attached — and presses send. Nothing here runs on a timer, for the same
 * reason nothing else in PROVIA does.
 *
 * Anybody who has asked not to be contacted is greyed out and cannot be
 * ticked. That is enforced on the server too: `contactable` is computed there
 * and the send refuses on the same flag, so the screen and the API cannot
 * drift into disagreeing about who may be written to.
 */
export default function Leads() {
  const toast = useToast();

  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const { data, loading, reload } = useApi<LeadListResponse>('/admin/leads', {
    status: status || undefined,
    q: query || undefined,
  });
  const { data: templateData } = useApi<EmailTemplateList>(
    '/admin/email/templates', { audience: 'leads' },
  );

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [tab, setTab] = useState('pipeline');

  const [composing, setComposing] = useState(false);
  const [detail, setDetail] = useState<LeadDetail | null>(null);
  const [adding, setAdding] = useState(false);
  const [newLead, setNewLead] = useState(BLANK_LEAD);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Lead | null>(null);
  const [calling, setCalling] = useState<Lead | null>(null);

  const leads = useMemo(() => data?.leads ?? [], [data]);
  const selectedIds = useMemo(
    () => leads.filter((l) => selected[l.id] && l.contactable).map((l) => l.id),
    [leads, selected],
  );

  async function addLead() {
    setBusy(true);
    try {
      await api.post('/admin/leads', newLead);
      setAdding(false);
      setNewLead(BLANK_LEAD);
      reload();
      toast.success('Added');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That lead could not be added');
    } finally {
      setBusy(false);
    }
  }

  async function setLeadStatus(lead: Lead, next: LeadStatus) {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status: next });
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be changed');
    }
  }

  async function stopContacting(lead: Lead) {
    try {
      await api.patch(`/admin/leads/${lead.id}`, {
        do_not_contact: true,
        do_not_contact_reason: 'Marked in the console',
      });
      reload();
      toast.success(`${lead.name} will not be written to again`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be changed');
    }
  }

  async function removeLead(lead: Lead) {
    try {
      await api.del(`/admin/leads/${lead.id}`);
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That lead could not be removed');
    }
  }

  async function openDetail(lead: Lead) {
    try {
      setDetail(await api.get<LeadDetail>(`/admin/leads/${lead.id}`));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That lead could not be opened');
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leads"
        subtitle="People who have not asked to hear from us. Nothing here sends on its own."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
              Add by hand
            </Button>
            <Button
              variant="primary"
              icon={<Send className="h-4 w-4" />}
              disabled={selectedIds.length === 0}
              onClick={() => setComposing(true)}
            >
              Write to {selectedIds.length || ''}
            </Button>
          </div>
        }
      />

      {/*
        Two tabs, and the playbook is not hidden in a help link.

        PROVIA does not search for leads, so an empty list is the normal first
        experience of this tab. "Where do I find these people" is the question
        somebody is actually holding at that moment, and the answer belongs one
        click away rather than in a document somewhere else.
      */}
      <Tabs
        tabs={[
          { id: 'pipeline', label: `Pipeline${data ? ` (${data.counts.total})` : ''}` },
          { id: 'playbook', label: 'How to find and win leads' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'playbook' ? <LeadPlaybook /> : (
        <>
      {/* ── The list ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <StatusPill active={!status} onClick={() => setStatus('')}>
            All {data?.counts.total ?? 0}
          </StatusPill>
          {(data?.statuses ?? []).map((s) => (
            <StatusPill key={s} active={status === s} onClick={() => setStatus(s)}>
              {s} {data?.counts[s] ?? 0}
            </StatusPill>
          ))}
        </div>
        <SearchInput value={query} onChange={setQuery} placeholder="Name, address, town…" />
      </div>

      {loading && !data ? <LoadingBlock rows={6} /> : leads.length === 0 ? (
        <EmptyState
          icon={<Target className="h-6 w-6" />}
          title="No leads yet"
          message="PROVIA does not go looking for them — you do, and the playbook says
                   where. An hour there fills this list for a month."
          action={
            <Button
              variant="secondary"
              icon={<BookOpen className="h-4 w-4" />}
              onClick={() => setTab('playbook')}
            >
              How to find leads
            </Button>
          }
        />
      ) : (
        <Card className="p-0 overflow-hidden">
          <ul className="divide-y divide-line">
            {leads.map((lead) => (
              <li
                key={lead.id}
                className={lead.contactable ? 'p-4 sm:p-5' : 'p-4 sm:p-5 opacity-60'}
              >
                <div className="flex items-start gap-3">
                  <div className="pt-0.5">
                    {/*
                      Selectable even when they cannot be written to.

                      Ticking a lead is not the same act as emailing one, and
                      tying the two together made the feature that finds a
                      missing address unreachable for every lead missing one.
                      Writing is still gated: `selectedIds` drops anyone who
                      is not contactable, and the send refuses them again on
                      the server.
                    */}
                    <Checkbox
                      checked={Boolean(selected[lead.id])}
                      onChange={(e) => setSelected((s) => ({ ...s, [lead.id]: e.target.checked }))}
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => void openDetail(lead)}
                        className="font-medium text-ink hover:text-brand transition-colors truncate"
                      >
                        {lead.name}
                      </button>
                      <Badge tone={STATUS_TONE[lead.status]}>{lead.status}</Badge>
                      {lead.do_not_contact && (
                        <Badge tone="danger">
                          <span className="flex items-center gap-1">
                            <Ban className="h-3 w-3" /> do not contact
                          </span>
                        </Badge>
                      )}
                      {/*
                        Sole trader, in red, because it is the one status that
                        changes what you are allowed to do rather than how
                        likely they are to reply: under PECR a sole trader
                        needs consent where a limited company does not.
                      */}
                      {lead.company_type === 'sole_trader' && (
                        <Badge tone="danger">sole trader — needs consent</Badge>
                      )}
                      {lead.company_type === 'limited_company' && (
                        <Badge tone="ok">
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {lead.company_number ?? 'limited company'}
                          </span>
                        </Badge>
                      )}
                      {lead.contact_count > 0 && (
                        <span className="text-xs text-faint">
                          written to {lead.contact_count}×
                          {lead.last_contacted_at && <>, last {relative(lead.last_contacted_at)}</>}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-muted">
                      {lead.email ? (
                        <span className="flex items-center gap-1.5 truncate">
                          <Mail className="h-3.5 w-3.5 text-faint shrink-0" />{lead.email}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-faint">
                          <Mail className="h-3.5 w-3.5 shrink-0" />no address found
                        </span>
                      )}
                      {lead.phone && (
                        <span className="flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5 text-faint shrink-0" />{lead.phone}
                        </span>
                      )}
                      {lead.website && (
                        <a
                          href={lead.website}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="flex items-center gap-1.5 hover:text-ink transition-colors truncate"
                        >
                          <Globe className="h-3.5 w-3.5 text-faint shrink-0" />
                          {lead.domain}
                          <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      )}
                    </div>

                    {lead.do_not_contact_reason && (
                      <p className="text-xs text-danger mt-1.5">{lead.do_not_contact_reason}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <Select
                      value={lead.status}
                      onChange={(e) => void setLeadStatus(lead, e.target.value as LeadStatus)}
                      className="h-9 text-xs w-32"
                    >
                      {(data?.statuses ?? []).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </Select>
                    {/*
                      Only where there is a number and they have not asked us
                      to stop. A Call button on a row that cannot be called is
                      an invitation to find out the hard way.
                    */}
                    {lead.phone && !lead.do_not_contact && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<PhoneCall className="h-4 w-4" />}
                        title={`Call ${lead.name} — rings your phone first`}
                        onClick={() => setCalling(lead)}
                      />
                    )}
                    {!lead.do_not_contact && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Ban className="h-4 w-4" />}
                        title="Never write to this business again"
                        onClick={() => void stopContacting(lead)}
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Trash2 className="h-4 w-4" />}
                      onClick={() => setDeleting(lead)}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

        </>
      )}

      {calling && (
        <CallPanel
          target={{ leadId: calling.id, name: calling.name, phone: calling.phone }}
          onClose={() => setCalling(null)}
          /*
           * Reloaded because a call can change the lead as well as the call:
           * saying "not interested" on the phone marks them do-not-contact,
           * and a row that still looks contactable afterwards is a row
           * somebody will ring again.
           */
          onChanged={reload}
        />
      )}

      {composing && (
        <Compose
          leadIds={selectedIds}
          templates={templateData?.templates ?? []}
          senders={data?.senders ?? []}
          defaultSender={data?.default_sender}
          replyTo={data?.reply_to ?? ''}
          onClose={() => setComposing(false)}
          onSent={() => {
            setComposing(false);
            setSelected({});
            reload();
          }}
        />
      )}

      {/* ── One lead ──────────────────────────────────────────────────── */}
      <Modal
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        title={detail?.lead.name ?? ''}
        subtitle={detail?.lead.source_detail
          ? `Found: ${detail.lead.source_detail}`
          : 'Added by hand'}
        size="lg"
      >
        {detail && (
          <div className="space-y-5">

            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Fact label="Email" value={detail.lead.email ?? '—'} />
              <Fact label="Phone" value={detail.lead.phone ?? '—'} />
              <Fact label="Website" value={detail.lead.domain ?? '—'} />
              <Fact label="Town" value={detail.lead.city ?? '—'} />
            </div>

            <div>
              <p className="text-sm font-medium text-ink mb-2">
                What we have sent them ({detail.emails.length})
              </p>
              {detail.emails.length === 0 ? (
                <p className="text-sm text-faint">Nothing yet.</p>
              ) : (
                <ul className="space-y-2">
                  {detail.emails.map((e) => (
                    <li key={e.id} className="rounded-xl border border-line p-3">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-ink truncate">{e.subject}</span>
                        <Badge tone={e.status === 'sent' ? 'ok' : e.status === 'failed' ? 'danger' : 'neutral'}>
                          {e.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-faint mt-1">
                        from {e.from_address} · {relative(e.created_at)}
                        {e.bounced_at && <span className="text-danger"> · bounced</span>}
                      </p>
                      <p className="text-sm text-muted whitespace-pre-wrap mt-2 line-clamp-6">
                        {e.body}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {detail.unsubscribe_url && (
              <p className="text-xs text-faint break-all">
                Their unsubscribe link: {detail.unsubscribe_url}
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* ── Adding one by hand ────────────────────────────────────────── */}
      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add a lead"
        subtitle="A referral, a business card, a name off a sign"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!newLead.name.trim()}
              onClick={() => void addLead()}
            >
              Add
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Business" required>
            <Input
              value={newLead.name}
              onChange={(e) => setNewLead((l) => ({ ...l, name: e.target.value }))}
              placeholder="Riverside Lettings"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Contact" hint="A person, if you know one">
              <Input
                value={newLead.contact_name}
                onChange={(e) => setNewLead((l) => ({ ...l, contact_name: e.target.value }))}
              />
            </Field>
            <Field label="Town">
              <Input
                value={newLead.city}
                onChange={(e) => setNewLead((l) => ({ ...l, city: e.target.value }))}
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email">
              <Input
                type="email"
                value={newLead.email}
                onChange={(e) => setNewLead((l) => ({ ...l, email: e.target.value }))}
              />
            </Field>
            <Field label="Phone">
              <Input
                value={newLead.phone}
                onChange={(e) => setNewLead((l) => ({ ...l, phone: e.target.value }))}
              />
            </Field>
          </div>
          <Field label="Website">
            <Input
              value={newLead.website}
              onChange={(e) => setNewLead((l) => ({ ...l, website: e.target.value }))}
              placeholder="https://…"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="How did you find them?">
              <Select
                value={newLead.source}
                onChange={(e) => setNewLead((l) => ({
                  ...l, source: e.target.value as LeadSource,
                }))}
              >
                {(data?.sources ?? []).map((src) => (
                  <option key={src} value={src}>{SOURCE_LABEL[src]}</option>
                ))}
              </Select>
            </Field>
            <Field label="Where exactly" hint="The most useful thing to know later">
              <Input
                value={newLead.source_detail}
                onChange={(e) => setNewLead((l) => ({ ...l, source_detail: e.target.value }))}
                placeholder="Rightmove, Leeds LS6"
              />
            </Field>
          </div>

          {/*
            Not a detail, which is why it is on the form rather than an edit
            somebody makes later: it decides whether writing to them at all is
            lawful, and the moment you are looking at their website is the
            moment the answer is in front of you.
          */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field
              label="Company type"
              hint="Their footer says. A limited company must print its number."
            >
              <Select
                value={newLead.company_type}
                onChange={(e) => setNewLead((l) => ({
                  ...l, company_type: e.target.value as LeadCompanyType,
                }))}
              >
                {(data?.company_types ?? []).map((t) => (
                  <option key={t} value={t}>{COMPANY_TYPE_LABEL[t]}</option>
                ))}
              </Select>
            </Field>
            <Field label="Company number" hint="Optional — makes the above checkable">
              <Input
                value={newLead.company_number}
                onChange={(e) => setNewLead((l) => ({ ...l, company_number: e.target.value }))}
                placeholder="08123456"
              />
            </Field>
          </div>

          {newLead.company_type === 'sole_trader' && (
            <div className="flex items-start gap-2 text-sm text-warn bg-warn/10
                            border border-warn/25 rounded-xl px-3 py-2.5">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                A sole trader counts as an individual under PECR, so marketing email
                needs their consent or the soft opt-in. Recording them is fine —
                writing to them cold is not.
              </span>
            </div>
          )}

          <Field label="Notes">
            <Textarea
              rows={3}
              value={newLead.notes}
              onChange={(e) => setNewLead((l) => ({ ...l, notes: e.target.value }))}
            />
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && void removeLead(deleting)}
        title="Remove this lead?"
        message={`"${deleting?.name}" is removed from the list. If they turn up in a later `
          + 'search they will be added again as a new lead — which is why anyone who has '
          + 'asked not to be contacted cannot be removed at all.'}
        confirmLabel="Remove"
      />
    </div>
  );
}

/**
 * Writing to the leads that were ticked.
 *
 * Same arrangement as the customer broadcast: you cannot send without a
 * preview, and the preview resolves the audience on the server using the same
 * function the send uses — so the list approved here is by construction the
 * list written to.
 */
function Compose({ leadIds, templates, senders, defaultSender, replyTo, onClose, onSent }: {
  leadIds: string[];
  templates: EmailTemplateList['templates'];
  senders: string[];
  defaultSender?: { address: string; name: string };
  replyTo: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const toast = useToast();
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fromAddress, setFromAddress] = useState(defaultSender?.address ?? '');
  const [preview, setPreview] = useState<LeadOutreachPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  function pickTemplate(id: string) {
    setTemplateId(id);
    setPreview(null);
    const chosen = templates.find((t) => t.id === id);
    if (chosen) {
      setSubject(chosen.subject);
      setBody(chosen.body);
      if (chosen.from_address) setFromAddress(chosen.from_address);
    }
  }

  function payload() {
    return {
      lead_ids: leadIds,
      template_id: templateId || undefined,
      subject,
      body,
      from_address: fromAddress || undefined,
    };
  }

  async function runPreview() {
    setBusy(true);
    try {
      setPreview(await api.post<LeadOutreachPreview>('/admin/leads/outreach/preview', payload()));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be previewed');
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    setSending(true);
    try {
      const res = await api.post<OutreachResult>('/admin/leads/outreach/send', payload());
      toast.success(`Sent ${res.sent} of ${res.recipients}`
        + (res.failed ? `, ${res.failed} failed` : '')
        + (res.skipped ? `, ${res.skipped} not sent — email is off` : ''));
      onSent();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send');
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Write to ${leadIds.length} ${leadIds.length === 1 ? 'lead' : 'leads'}`}
      subtitle="Nothing goes out until you have seen the preview"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="secondary"
            icon={<Eye className="h-4 w-4" />}
            loading={busy}
            disabled={!subject.trim() || !body.trim()}
            onClick={() => void runPreview()}
          >
            Preview
          </Button>
          <Button
            variant="primary"
            icon={<Send className="h-4 w-4" />}
            loading={sending}
            disabled={!preview || preview.total === 0 || preview.over_limit}
            onClick={() => void send()}
          >
            Send {preview ? `to ${preview.total}` : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Template" hint="Or write something below">
            <Select value={templateId} onChange={(e) => pickTemplate(e.target.value)}>
              <option value="">No template</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="From" hint="Must be on a domain we can send from">
            <Select
              value={fromAddress}
              onChange={(e) => { setFromAddress(e.target.value); setPreview(null); }}
            >
              {senders.map((address) => (
                <option key={address} value={address}>{address}</option>
              ))}
            </Select>
          </Field>
        </div>

        {replyTo && (
          <p className="text-xs text-faint">
            Replies go to <span className="text-muted">{replyTo}</span> — the sending address
            has no mailbox behind it.
          </p>
        )}

        <Field label="Subject">
          <Input
            value={subject}
            onChange={(e) => { setSubject(e.target.value); setPreview(null); }}
            placeholder="A quicker way to run {{business_name}}"
          />
        </Field>

        <Field label="Message" hint="The unsubscribe line is added for you — it cannot be left off.">
          <Textarea
            rows={10}
            value={body}
            onChange={(e) => { setBody(e.target.value); setPreview(null); }}
            className="min-h-[12rem] font-mono text-xs"
            placeholder={'Hello {{first_name}},\n\n…'}
          />
        </Field>

        {preview && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-ink font-medium">
                {preview.total} {preview.total === 1 ? 'recipient' : 'recipients'}
              </span>
              {preview.over_limit && <Badge tone="warn">over the {preview.limit} limit</Badge>}
            </div>

            <SkipNote skipped={preview.skipped} />

            {!preview.email_configured && (
              <Note>
                Email is not switched on. Sending now would write every message to the
                execution log instead of delivering it.
              </Note>
            )}

            {preview.unsubscribe_by_reply && (
              <Note>
                This deployment does not know its own public address, so the opt-out is
                “reply and ask” rather than a link. Set WEB_APP_URL to get a working link.
              </Note>
            )}

            {!preview.reply_to_header && (
              <Note>
                The mail provider in use cannot carry a Reply-To header, so {preview.reply_to
                  || 'the reply address'} is written into the message instead of being
                attached to it. For outreach that matters — a reply is the whole point.
                Resend or SendGrid carry it.
              </Note>
            )}

            {preview.sample && (
              <div className="rounded-xl border border-line overflow-hidden">
                <div className="px-3 py-2 bg-raised border-b border-line text-xs text-faint">
                  As {preview.sample.to} will see it, from {preview.from.address}
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-sm font-medium text-ink">{preview.sample.subject}</p>
                  <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">
                    {preview.sample.body}
                  </p>
                  <p className="text-xs text-faint border-t border-line pt-2 break-all">
                    {preview.sample.footer}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function SkipNote({ skipped }: { skipped: LeadOutreachPreview['skipped'] }) {
  const parts: string[] = [];
  if (skipped.no_email) parts.push(`${skipped.no_email} with no address`);
  if (skipped.do_not_contact) parts.push(`${skipped.do_not_contact} who asked us not to`);
  if (skipped.already_contacted) parts.push(`${skipped.already_contacted} already written to`);
  if (skipped.duplicate) parts.push(`${skipped.duplicate} sharing an address`);
  if (parts.length === 0) return null;
  return <p className="text-xs text-faint">Not included: {parts.join(', ')}.</p>;
}

/*
 * Classes written out rather than built from a prop.
 *
 * Tailwind extracts class names by scanning the source as text, so
 * `text-${tone}` produces markup referring to a class that was never
 * generated — the warning renders as plain paragraph text and nobody notices
 * until it matters. One tone, spelled out.
 */
function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-sm text-warn bg-warn/10
                    border border-warn/25 rounded-xl px-3 py-2.5">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-faint">{label}</p>
      <p className="text-sm text-ink truncate">{value}</p>
    </div>
  );
}

function StatusPill({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={active
        ? 'text-xs px-2.5 py-1 rounded-full border border-brand/40 bg-brand/15 text-ink font-medium capitalize'
        : 'text-xs px-2.5 py-1 rounded-full border border-line text-muted hover:text-ink transition-colors capitalize'}
    >
      {children}
    </button>
  );
}
